import { useQuery } from "@apollo/client";
import { Trans } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useStatsigClient } from "@statsig/react-bindings";
import { PlatformState, UniqueIdentifierState } from "MVVM/State/iframe";
import {
  NOTIFICATION_MESSAGING,
  GET_PROFILE_CLIP_COUNT,
} from "MVVM/GraphQL/partnerOverlay";
import {
  buildLink,
  getBestClaimableClip,
  getClaimableClipThumbnail,
} from "MVVM/Utilities/Player";
import { IClip, ESupportedGames } from "MVVM/@types";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { URLS } from "MVVM/Utilities/Maps";
import theme from "theme/themeThree";
import { TUserRole } from "../../@types";
import { IFRAME_PROMOTIONS } from "MVVM/GraphQL/promotions";
import { useState } from "react";
import { isPartner, Partner } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import { PlaylistIdState } from "MVVM/State/embeddablePlaylist";

import {
  filterPromotionsByExperiment,
  shouldShowExperimentPromotion,
} from "MVVM/Utilities/filterPromotions";
interface IPromotions {
  name: string;
  activeSiteVideoPlayerPromos: {
    name: string;
    title: string;
    subtitle: string;
    image: string;
    link: string;
    buttonCopy: string;
    experimentName: string;
  }[];
}

export const NotificationBannerViewModel = (
  clip: IClip,
  userRole: TUserRole = "creator",
) => {
  const { getExperiment } = useStatsigClient();
  const platform = useRecoilValue(PlatformState);
  const uid = useRecoilValue(UniqueIdentifierState);
  const domain = window.location.origin;
  const isHLTV = isPartner(platform, Partner.HLTV);
  const isUGG = isPartner(platform, Partner.UGG);
  const isLeetify = isPartner(platform, Partner.LEETIFY);
  const isTrn = isPartner(platform, Partner.TRN);
  const isGamersClub = isPartner(platform, Partner.GC);
  const playlistId = useRecoilValue(PlaylistIdState);
  const [fetchedClaimClips, setFetchedClaimClips] = useState(false);
  const showProfileCTA = getExperiment("hltv_pro_player_profile_ctas").get(
    "show_profile_cta",
    false,
  );

  const { data, loading, error } = useQuery(NOTIFICATION_MESSAGING, {
    variables: {
      steamId: uid || clip.playerGameIdentifier,
      puuid:
        getMatchHistoryGameEnum(clip.game) === ESupportedGames.LOL
          ? clip.playerGameIdentifier
          : uid || clip.playerGameIdentifier,
      partner: platform,
    },
    onError() {
      return;
    },
    onCompleted() {
      setFetchedClaimClips(true);
    },
  });

  const messageData = data?.suggestedClips;
  const claimableCount = messageData?.clips?.length;

  let notificationData = null;

  if (
    isHLTV &&
    showProfileCTA &&
    clip.username &&
    clip.username !== "Allstar.gg"
  ) {
    const profileUrl = `${domain}/u/${
      clip.playerGameIdentifier
    }?utm_source=${platform}&ajs_aid=${window.rudderanalytics.getAnonymousId()}`;

    const playerPhoto = clip.metadata?.find(
      (m: { key: string; value: string }) => m.key === "playerPhoto",
    )?.value;

    const bannerImage = playerPhoto || clip.thumbnailStandardUrl || "";

    const smallScreenText = (
      <Trans
        i18nKey="embeddablePlayer.notificationBanner.exploreProfile.smallScreen"
        defaults="Explore all clips from <styledSpan>{{username}}</styledSpan>"
        values={{ username: clip.username }}
        components={{
          styledSpan: <span style={{ color: theme.colors.envy }} />,
        }}
      />
    );

    const largeScreenText = (
      <Trans
        i18nKey="embeddablePlayer.notificationBanner.exploreProfile.largeScreen"
        defaults="Explore all clips from <styledSpan>{{username}}</styledSpan>"
        values={{ username: clip.username }}
        components={{
          styledSpan: <span style={{ color: theme.colors.envy }} />,
        }}
      />
    );

    notificationData = {
      buttonLink: profileUrl,
      smallScreenText,
      largeScreenText,
      thumbnail: bannerImage,
      showPlayButton: false,
      promotionName: "hltv_profile_cta",
    };
  } else if (claimableCount) {
    const bestClaimableClip = getBestClaimableClip(
      messageData?.clips,
      platform,
    );
    const bestId = bestClaimableClip?._id ?? null;
    const thumbnail = bestClaimableClip
      ? getClaimableClipThumbnail(bestClaimableClip) || ""
      : "";
    const link = buildLink({
      url: `${domain}/register?utm_source=${
        clip.developer?.slug ||
        new URLSearchParams(window.location.search).get("platform") ||
        "unknown"
      }`,
      game: getMatchHistoryGameEnum(clip.game),
      numClaimableClips: claimableCount,
      username: clip.username,
      clipThumbnail: thumbnail,
      bestClipId: bestId,
      loggedInRedirect: `/clip?clip=${clip.internalId}`,
      platform: platform,
    });

    const smallScreenText = (
      <Trans
        i18nKey="embeddablePlayer.notificationBanner.claimClips.smallScreen"
        defaults="<styledSpan>{{count}} More</styledSpan> on Allstar.gg"
        values={{ count: claimableCount }}
        components={{
          styledSpan: (
            <span style={{ color: theme.colors.envy }}>
              {claimableCount} More
            </span>
          ),
        }}
      />
    );

    const largeScreenText = (
      <Trans
        i18nKey="embeddablePlayer.notificationBanner.claimClips.largeScreen"
        defaults="You have <styledSpan>{{count}} More Clips</styledSpan> to Claim on Allstar.gg"
        values={{ count: claimableCount }}
        components={{
          styledSpan: (
            <span style={{ color: theme.colors.envy }}>
              {claimableCount} More Clips
            </span>
          ),
        }}
      />
    );

    notificationData = {
      bestId,
      thumbnail,
      buttonLink: link,
      smallScreenText,
      largeScreenText,
      showPlayButton: true,
    };
  } else {
    const uniqueId = uid || clip.playerGameIdentifier;

    const url = !isTrn
      ? `${URLS.MATCH_HISTORY}?game=${getMatchHistoryGameEnum(
          clip.game,
        )}&playerSearchIdentifier=${uniqueId}&partner=${platform}&ajs_aid=${window.rudderanalytics.getAnonymousId()}&utm_source=${
          clip.developer?.slug ||
          new URLSearchParams(window.location.search).get("platform") ||
          "unknown"
        }`
      : `${window.location.origin}/clip/${
          clip.shareId
        }?ajs_aid=${window.rudderanalytics.getAnonymousId()}&utm_source=${
          clip.developer?.slug || platform || "unknown"
        }&context=playlist&playlistId=${playlistId}`;

    const smallScreenText = (
      <Trans
        i18nKey={`partnerOverlay.notificationBanner.createMoreMoments.smallScreen${
          userRole === "viewer" ? "Watch" : ""
        }`}
        defaults={`${
          userRole === "viewer" ? "Watch" : "Clip"
        } <styledSpan>10+ more moments</styledSpan> from this match.`}
        components={{
          styledSpan: (
            <span style={{ color: theme.colors.envy }}>10+ other moments</span>
          ),
        }}
      />
    );

    const largeScreenText = (
      <Trans
        i18nKey={`partnerOverlay.notificationBanner.createMoreMoments.largeScreen${
          userRole === "viewer" ? "Watch" : ""
        }`}
        defaults={`${
          userRole === "viewer" ? "Watch" : "You can create"
        } <styledSpan>10+ other moments</styledSpan> from this match.`}
        components={{
          styledSpan: (
            <span style={{ color: theme.colors.envy }}>10+ other moments</span>
          ),
        }}
      />
    );

    notificationData = {
      buttonLink: url,
      smallScreenText,
      largeScreenText,
      thumbnail: "static/matchhistory.png",
      showPlayButton: false,
    };
  }

  const {
    data: promotionsData,

    loading: promotionsLoading,
    error: promotionsError,
  } = useQuery<{ promotions: IPromotions }>(IFRAME_PROMOTIONS, {
    skip: !fetchedClaimClips || claimableCount > 0 || !isHLTV || showProfileCTA,
  });

  if (
    promotionsData &&
    promotionsData?.promotions?.activeSiteVideoPlayerPromos?.length > 0
  ) {
    const filteredPromotions = filterPromotionsByExperiment(
      promotionsData?.promotions?.activeSiteVideoPlayerPromos,
      (experimentName) =>
        shouldShowExperimentPromotion(experimentName, getExperiment),
      getExperiment,
    );

    const promotion = filteredPromotions?.[0];

    if (promotion) {
      notificationData = {
        smallScreenText: promotion.title,
        largeScreenText: promotion.subtitle,
        thumbnail: promotion.image,
        buttonLink: promotion.link,
        buttonCopy: promotion.buttonCopy,
        promotionName: promotion.name,
      };
    }
  }

  return {
    notificationData,
    loading,
    error,
    promotionsData: promotionsData?.promotions,
    promotionsLoading,
    promotionsError,
    fetchedClaimClips,
    setFetchedClaimClips,
    showProfileCTA,
  };
};
